import moment from "moment"
import { startOfDay, format, endOfDay, addHours } from "date-fns";
type Range = {
	start: Date, end: Date
}
export type Resolution = "HOURLY" | "DAILY" | "MONTHLY" | "YEARLY"
export enum resolutionOfRange {
	hour = 0,
	day = 59,
	month = 729,
	year = 730
}

export const HOURLY = "HOURLY"
export const DAILY = "DAILY"
export const MONTHLY = "MONTHLY"
export const YEARLY = "YEARLY"
export const koreaOffset = new Date().getTimezoneOffset() / 60;
export const getResolutionOfRange = (range: Range) => {
	const { start, end } = range;
	const days = moment(end).diff(moment(start), "days")

	if (days === 0) {
		return HOURLY
	} else if (days <= resolutionOfRange.day) {
		return DAILY
	} else if (days <= resolutionOfRange.month) {
		return MONTHLY
	} else {
		return YEARLY
	}
}

export const getAllDateInRange = (range: Range, resolution: Resolution) => {
	const { start, end } = range;
	let distance = 0;
	let resolutionOfMoment: "hours" | 'days' | 'months' | "year" = "hours"
	if (resolution === "HOURLY") {
		distance = moment(end).diff(moment(start), "hours")
	} else if (resolution === "DAILY") {
		distance = moment(end).diff(moment(start), "days")
		resolutionOfMoment = "days"
	} else if (resolution === "MONTHLY") {
		distance = moment(end).diff(moment(start), "months")
		resolutionOfMoment = "months"
	} else {
		distance = moment(end).diff(moment(start), "year")
		resolutionOfMoment = "year"
	}

	const dateRange: Date[] = [];
	const lengthOfBetweenRange = distance + 1 - 2;//고정된 처음과 끝 2

	for (let i = 0; i < lengthOfBetweenRange; i++) {
		const date = moment(start).add(i + 1, resolutionOfMoment).toDate();
		dateRange.push(date);
	}

	return [
		start,
		...dateRange,
		end
	]
}

export const getChartDateKey = (date: Date, resolution: Resolution) => {
	if (resolution === "HOURLY") {
		return format(date, "yyyy-M-d-H")
	} else if (resolution === "DAILY") {
		return format(date, "yyyy-M-d") + "-null"
	} else if (resolution === 'MONTHLY') {
		return format(date, "yyyy-M") + "-null-null"
	} else {
		return format(date, "yyyy") + "-null-null-null"
	}
}

export const xAxisLabel = (resolution: Resolution, range: Date[]) => {
	let format = "HH시"

	let midiumFormat = format;
	if (resolution !== "HOURLY") {
		format = "yy.M.D"
	}
	if (resolution === "YEARLY") {
		midiumFormat = "yyyy"
	} else if (resolution === "MONTHLY") {
		midiumFormat = "yyyy.MM"
	} else if (resolution === "DAILY") {
		midiumFormat = "MM.D"
	}

	return (axis: "start" | "midium" | "end") => {
		if (axis === "start") {
			return moment(range[0]).format(format)
		} else if (axis === "midium") {
			const middle = Math.floor(range.length / 2);
			if (middle === 0) { return "" }
			return moment(range[middle]).format(midiumFormat)
		} else {
			return moment(range[range.length - 1]).format(format)
		}
	}
}

export const chartToolTipDateFormat = (date: Date, resolution: Resolution, forceFormat?: string) => {
	let format = "yyyy.MM.DD HH:00"
	if (resolution === "DAILY") {
		format = "yyyy.MM.DD"
	} else if (resolution === "MONTHLY") {
		format = "yyyy.MM"
	} else if (resolution === "YEARLY") {
		format = "yyyy"
	}

	return moment(date).format(forceFormat || format)
}

export function processingDate(time: string | undefined, format = "yyyy.MM.DD HH:mm") {
	if (!time) return "";

	return !time
		? ""
		: moment(new Date(time + "Z").toISOString()).format(format);
}

export const intervalToUTC = (range: Interval, offset = koreaOffset) => {
	const startUTC = format(
		addHours(startOfDay(range.start), offset),
		"yyyy-MM-dd|HH:mm:ss"
	).replace("|", "T");

	const endUTC = format(
		addHours(endOfDay(range.end), offset),
		"yyyy-MM-dd|HH:mm:ss"
	).replace("|", "T");

	return {
		startUTC,
		endUTC,
	};
}

export async function delay(milSecond: number) {
	return new Promise((resolve) => {
		setTimeout(() => { resolve("") }, milSecond)
	})
}
export function timeStringToDate(startTime: string, endTime: string) {
	if (!(startTime || endTime)) {
		return {
			startTime: new Date(),
			endTime: new Date()
		}
	}
	if ((startTime || endTime).includes('-' || '_')) {
		const parseStartTime = startTime.replace(/-,_/gi, ".")
		const parseEndTime = endTime.replace(/-,_/gi, ".")
		return {
			startTime: moment(parseStartTime as string, "yyyy.MM.DD").toDate(),
			endTime: moment(parseEndTime as string, "yyyy.MM.DD").toDate()
		}
	}
	if (startTime > endTime) {
		return {
			startTime: moment(endTime as string, "yyyy.MM.DD").toDate(),
			endTime: moment(endTime as string, "yyyy.MM.DD").toDate()
		}
	}

	return {
		startTime: moment(startTime as string, "yyyy.MM.DD").toDate(),
		endTime: moment(endTime as string, "yyyy.MM.DD").toDate(),
	}
}

export function yyyymmddToDate(yyyymmdd: string) {
	const year = parseInt(yyyymmdd.substring(0, 4), 10);
	const month = parseInt(yyyymmdd.substring(4, 6), 10) - 1; // 월은 0부터 시작하므로 1을 빼줍니다.
	const day = parseInt(yyyymmdd.substring(6, 8), 10);

	return new Date(year, month, day);
}

export function getDateDiff(receivedDate: string) {
	// 특정 날짜를 나타내는 Date 객체 생성
	const specificDate = yyyymmddToDate(receivedDate); // yyyymmdd를 하이픈 추가하여 Date객체 생성

	// 오늘 날짜를 나타내는 Date 객체 생성
	const today = new Date();

	// 오늘 날짜로부터 1일 전의 날짜를 계산
	const oneDayBefore = new Date();
	oneDayBefore.setDate(today.getDate() - 1);

	// 오늘 날짜로부터 5일 전의 날짜를 계산
	const fiveDaysBefore = new Date();
	fiveDaysBefore.setDate(today.getDate() - 5);

	// 특정 날짜가 오늘로부터 1일 이내인지 확인
	if (oneDayBefore <= specificDate && specificDate <= today) {
		console.log("특정 날짜는 오늘로부터 1일 이내에 있습니다.");
		return "withinOneDay"
	}
	// 특정 날짜가 5일전보다 크고 1일 이내인지 확인
	else if (fiveDaysBefore <= specificDate && specificDate <= today) {
		console.log("특정 날짜는 오늘로부터 1일 이상 5일 이내에 있습니다.");
		return "betweenOneAndFive"
	}
	// 특정 날짜가 5일전인지 확인
	else if (specificDate < fiveDaysBefore) {
		console.log("특정 날짜는 오늘로부터 5일 이상 지났습니다.");
		return "moreThanFive"
	} else {
		console.log("")
	}
}

